import { useEffect, useLayoutEffect, useRef } from 'react';
import { ownsKeys } from '../projectGraph/shortcuts';
import { auditKeyFor, isModalOpen } from './auditKeys';

export type AuditShortcutActions = { digit: (key: string) => void; submit: () => void };

/**
 * The audit keys (1-7, Enter) on the document until the form unmounts. A key typed into a field, pressed with a
 * modifier, inside or under an open dialog, or held (repeat) is left alone; only a key the form acts on is prevented.
 */
export function useAuditShortcuts(actions: AuditShortcutActions) {
  // The latest actions, so the listener is added once.
  const latest = useRef(actions);
  useLayoutEffect(() => {
    latest.current = actions;
  });
  useEffect(() => {
    const keyDown = (event: KeyboardEvent) => {
      if (event.repeat || event.defaultPrevented) return;
      const target = event.target instanceof HTMLElement ? event.target : null;
      const key = auditKeyFor(event.key, target?.tagName ?? '', {
        modified: event.ctrlKey || event.metaKey || event.altKey,
        ownsKeys: target !== null && ownsKeys(target),
        modalOpen: isModalOpen(document),
      });
      if (key === null) return;
      event.preventDefault();
      if (key.kind === 'submit') latest.current.submit();
      else latest.current.digit(key.key);
    };
    document.addEventListener('keydown', keyDown);
    return () => document.removeEventListener('keydown', keyDown);
  }, []);
}
